// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectsDetailUpdate.tsx
// [概要] 営業案件 詳細と更新 CloudSQL API リクエスト
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from "../firebase/AuthProvider";
import { usePermissions } from "../firebase/PermissionProvider";
import axios from 'axios';
import LoadingView from '../components/LoadingView';
import {
  Container,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  Stack,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Link,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
  getMetadata
} from 'firebase/storage';
import { storage } from '../firebase/firebase';
import * as Icons from "../constants/icons";
import SalesProjectFormFields from '../components/SalesProjectFormFields';
import SalesProjectPhaseChip from '../components/SalesProjectPhaseChip';
import SalesProjectTypeChip from '../components/SalesProjectTypeChip';
import SalesProjectPhaseHistory from '../components/SalesProjectPhaseHistory';
import SalesProjectLostReasonDialog, { LostReasonValue } from '../components/SalesProjectLostReasonDialog';
import CrmCustomerForm from '../components/CrmCustomerForm';
import { FIRE_INSURANCE_AMOUNT, FIRE_INSURANCE_PAYEE, PROJECT_TYPE_LABELS } from '../constants/masterData';
import { validateSalesProject, ValidationErrors } from '../utils/validation';
import { sqmToTsubo } from '../utils/land';
import { useMasterData } from '../providers/MasterDataProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';
import ProjectBuildingLinks from '../components/ProjectBuildingLinks';

interface SalesProjectsDetailUpdateProps {
  projectId?: string;
  onClose?: () => void;
  onSaved?: () => void; // 保存成功時に親へ通知
}

// 詳細項目の表示用: 上段にラベル(グレー)、下段に値(黒)
const DetailField: React.FC<{
  label: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  multiline?: boolean;
}> = ({ label, icon, children, multiline }) => {
  const isEmpty = children === null || children === undefined || children === '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'grey.500' }}>
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { fontSize: '0.95rem' } }}>
            {icon}
          </Box>
        )}
        <Typography variant="caption" sx={{ color: 'grey.500', fontSize: '0.75rem', lineHeight: 1.4 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        color="text.primary"
        sx={{ whiteSpace: multiline ? 'pre-wrap' : 'normal', minHeight: '1.5em' }}
      >
        {isEmpty ? '-' : children}
      </Typography>
    </Box>
  );
};

export default function SalesProjectsDetailUpdate({
  projectId: propProjectId,
  onClose,
  onSaved,
}: SalesProjectsDetailUpdateProps) {
  const { staffList, loading: masterLoading } = useMasterData();

  // /////////////////////////////////////////////////////////////
  // 📥変数インスタンス
  // /////////////////////////////////////////////////////////////
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Propsがあればそれを優先、なければURLから取得
  const queryParams = new URLSearchParams(location.search);
  const projectId = propProjectId || queryParams.get('id');

  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [inputGroup, setInputGroup] = useState('basic');

  // 編集モード関連の状態
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [editTab, setEditTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // 活動履歴フォーム関連
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState({ open: false, message: '' });
  const [editingActivity, setEditingActivity] = useState<any>(null); // nullなら新規、データがあれば編集
  const [activityForm, setActivityForm] = useState({
    activity_name: '',
    due_date: format(new Date(), 'yyyy-MM-dd'),
    assign_to_id: '',
    assign_to: '',
    description: '',
    activity_content: ''
  });
  const [assignToInput, setAssignToInput] = useState('');

  // メニュー関連
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuActivity, setMenuActivity] = useState<any>(null);

  // バリデーションエラー
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // 権限取得
  const { permissionData, loading: permsLoading } = usePermissions();
  const permissions = permsLoading ? null : permissionData;
  const viewLevel = !permissions
    ? 'individual'
    : permissions.includes('readdata.sfa.all_view')
      ? 'all'
      : permissions.includes('readdata.sfa.branch_view')
        ? 'branch'
        : 'individual';

  // ダイアログ・スナックバーの状態
  const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = useState(false);
  // 失注理由入力ダイアログ（フェーズを 7.失注 へ変更して保存する際に必須入力）
  const [lostReasonDialogOpen, setLostReasonDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // --------------------------------------------------
  // 📥 Firebase Storage関連の状態
  // --------------------------------------------------
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string, name: string, contentType?: string } | null>(null);
  // GCS(Storage)からのプレビュー取得にはラグがあるため、img/iframeのonLoad完了まで
  // ローディングをオーバーレイ表示する。プレビュー対象が変わるたびにtrueへ戻す。
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any>(null);

  // 取引先（顧客）詳細モーダル
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  // --------------------------------------------------
  // 🌐 Cloud Functions URL
  // --------------------------------------------------
  const DETAIL_FUNCTION_URL = ep('get_sales_project_detail');
  const UPDATE_FUNCTION_URL = ep('update_sales_project');
  const ACTIVITIES_FUNCTION_URL = ep('get_activity_history');
  const CREATE_ACTIVITY_URL = ep('create_activity');
  const UPDATE_ACTIVITY_URL = ep('update_activity_history');
  const DELETE_ACTIVITY_URL = ep('delete_activity_history');


  // /////////////////////////////////////////////////////////////
  // {} メソッド
  // /////////////////////////////////////////////////////////////

  // --------------------------------------------------
  // 📥 案件詳細データを取得
  // --------------------------------------------------
  const fetchDetail = async () => {
    if (!projectId) return;

    try {
      setLoadingInfo({ open: true, message: '読み込み中...' });
      setError(null);
      const res = await axios.post(DETAIL_FUNCTION_URL, {
        projectId: projectId
      });

      if (res.data.status === 'success') {
        setResponse(res.data.detaildata);
      } else {
        setError(res.data.message || 'データの取得に失敗しました');
      }
    } catch (err: any) {
      console.error("Error fetching project detail:", err);
      setError(err.message || '通信エラーが発生しました');
    } finally {
      setLoadingInfo({ open: false, message: '' });
    }
  };

  useEffect(() => {
    setIsEditing(false); // 案件が切り替わったら編集モードを解除
    fetchDetail();
    fetchActivities();
    fetchFiles();
  }, [projectId]);

  // --------------------------------------------------
  // 📥 ストレージからファイル一覧を取得
  // --------------------------------------------------
  const fetchFiles = async () => {
    if (!projectId) return;

    try {
      setFilesLoading(true);
      const listRef = ref(storage, `sales-projects/${projectId}`);
      const res = await listAll(listRef);

      const fileData = await Promise.all(
        res.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const metadata = await getMetadata(item);
          return {
            name: item.name,
            fullPath: item.fullPath,
            url,
            size: metadata.size,
            contentType: metadata.contentType,
            updated: metadata.updated
          };
        })
      );

      // 日付順にソート（新しい順）
      fileData.sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime());
      setUploadedFiles(fileData);
    } catch (err: any) {
      console.error("Error fetching files:", err);
    } finally {
      setFilesLoading(false);
    }
  };

  // --------------------------------------------------
  // 📤 ファイルアップロード
  // --------------------------------------------------
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !projectId) return;

    try {
      setUploading(true);
      setLoadingInfo({ open: true, message: 'アップロード中...' });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileRef = ref(storage, `sales-projects/${projectId}/${file.name}`);
        await uploadBytes(fileRef, file);
      }

      setSnackbar({ open: true, message: `${files.length}件のファイルをアップロードしました`, severity: 'success' });
      fetchFiles(); // 一覧を更新
    } catch (err: any) {
      console.error("Error uploading files:", err);
      setSnackbar({ open: true, message: 'アップロードに失敗しました', severity: 'error' });
    } finally {
      setUploading(false);
      setLoadingInfo({ open: false, message: '' });
      // Inputのリセット
      e.target.value = '';
    }
  };

  // --------------------------------------------------
  // 🗑️ ファイル削除
  // --------------------------------------------------
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      setDeleteFileDialogOpen(false);
      setLoadingInfo({ open: true, message: '削除中...' });
      const fileRef = ref(storage, `sales-projects/${projectId}/${fileToDelete.name}`);
      await deleteObject(fileRef);

      setSnackbar({ open: true, message: 'ファイルを削除しました', severity: 'success' });
      fetchFiles(); // 一覧を更新
    } catch (err: any) {
      console.error("Error deleting file:", err);
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } finally {
      setLoadingInfo({ open: false, message: '' });
      setFileToDelete(null);
    }
  };

  // --------------------------------------------------
  // 📥 活動履歴を取得
  // --------------------------------------------------
  const fetchActivities = async () => {
    if (!projectId) return;

    try {
      setActivitiesLoading(true);
      const res = await axios.post(ACTIVITIES_FUNCTION_URL, {
        projectId: projectId
      });

      if (res.data.status === 'success') {
        setActivities(res.data.activities || []);
      }
    } catch (err: any) {
      console.error("Error fetching activities:", err);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // --------------------------------------------------
  // 💾 更新処理
  // --------------------------------------------------
  const handleSave = async () => {
    const errors = validateSalesProject(editFormData);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSnackbar({ open: true, message: '入力内容を確認してください', severity: 'error' });
      // 基本情報タブにエラーがある場合はタブを切り替える
      if (errors.project_name || errors.client_id || errors.project_type || errors.parent_project_id || errors.order_confirmed_date) {
        setEditTab('basic');
      }
      return;
    }

    // フェーズを 7.失注 へ変更して保存する場合は、先に失注理由の入力を必須とする
    const isTransitionToLost =
      editFormData?.phase === '7.失注' && response?.phase !== '7.失注';
    if (isTransitionToLost && !editFormData?.lost_reason_category) {
      setLostReasonDialogOpen(true);
      return;
    }

    setConfirmSaveDialogOpen(true);
  };

  // 失注理由ダイアログの確定 → フォームへ反映して保存確認へ進む
  const handleLostReasonConfirm = (value: LostReasonValue) => {
    setEditFormData((prev: any) => ({ ...prev, ...value }));
    setLostReasonDialogOpen(false);
    setConfirmSaveDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmSaveDialogOpen(false);
    try {
      setSaving(true);
      setLoadingInfo({ open: true, message: '保存中...' });
      const res = await axios.post(UPDATE_FUNCTION_URL, {
        projectId: projectId,
        formData: {
          ...editFormData,
          updated_operator_id: user?.email
        }
      });

      if (res.data.status === 'success') {
        // alert('保存しました');
        setSnackbar({ open: true, message: '保存しました', severity: 'success' });
        setIsEditing(false);
        fetchDetail(); // データを再取得
        if (onSaved) onSaved(); // 親コンポーネント（一覧など）へ保存成功を通知（一覧の再読込のみ。詳細画面は閉じない）
      } else {
        // alert('保存に失敗しました: ' + res.data.message);
        setSnackbar({ open: true, message: '保存に失敗しました: ' + res.data.message, severity: 'error' });
      }
    } catch (err: any) {
      console.error("Error updating project:", err);
      // alert('エラーが発生しました: ' + err.message);
      setSnackbar({ open: true, message: 'エラーが発生しました: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
      setLoadingInfo({ open: false, message: '' });
    }
  };

  // --------------------------------------------------
  // 📝 活動履歴の保存（新規・編集）
  // --------------------------------------------------
  const handleActivitySave = async () => {
    // バリデーション：入力があるがIDが未確定の場合
    if (assignToInput && !activityForm.assign_to_id) {
      setSnackbar({
        open: true,
        message: '担当者を候補一覧から正しく選択してください。',
        severity: 'error'
      });
      return;
    }

    try {
      setActivityDialogOpen(false); // 入力ダイアログを閉じる
      setLoadingInfo({ open: true, message: '保存中...' });

      if (editingActivity) {
        // 編集
        await axios.post(UPDATE_ACTIVITY_URL, {
          auto_number: editingActivity.auto_number,
          activityData: activityForm
        });
      } else {
        // 新規登録
        await axios.post(CREATE_ACTIVITY_URL, {
          ...activityForm,
          project_id: projectId
        });
      }
      fetchActivities(); // 再取得
    } catch (err: any) {
      console.error("Error saving activity:", err);
      // alert('保存に失敗しました');
      setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
    } finally {
      setLoadingInfo({ open: false, message: '' });
    }
  };

  // --------------------------------------------------
  // 🗑️ 活動履歴の削除
  // --------------------------------------------------
  const handleDeleteActivity = async () => {
    if (!menuActivity) return;
    try {
      setDeleteDialogOpen(false); // 確認ダイアログを閉じる
      setLoadingInfo({ open: true, message: '削除中...' });

      await axios.post(DELETE_ACTIVITY_URL, {
        auto_number: menuActivity.auto_number
      });
      setAnchorEl(null);
      fetchActivities(); // 再取得
    } catch (err: any) {
      console.error("Error deleting activity:", err);
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } finally {
      setLoadingInfo({ open: false, message: '' });
    }
  };

  // --------------------------------------------------
  // ✏️ 活動履歴の追加
  // --------------------------------------------------
  const handleOpenActivityDialog = (activity: any = null) => {
    if (activity) {
      setEditingActivity(activity);
      setActivityForm({
        activity_name: activity.activity_name || '',
        due_date: activity.due_date || format(new Date(), 'yyyy-MM-dd'),
        assign_to_id: activity.assign_to_id || '',
        assign_to: activity.assign_to || '',
        description: activity.description || '',
        activity_content: activity.activity_content || ''
      });
      setAssignToInput(activity.assign_to || '');
    } else {
      setEditingActivity(null);
      setActivityForm({
        activity_name: '',
        due_date: format(new Date(), 'yyyy-MM-dd'),
        assign_to_id: '',
        assign_to: '',
        description: '',
        activity_content: ''
      });
      setAssignToInput('');
    }
    setActivityDialogOpen(true);
    setAnchorEl(null);
  };

  // --------------------------------------------------
  // 📄 コンテンツ部分（モーダルとページで共通）
  // --------------------------------------------------
  const renderContent = () => {
    // --------------------------------------------------
    // 🔁 Loding view
    // --------------------------------------------------
    if (loadingInfo.open || masterLoading) {
      return <LoadingView message={masterLoading ? "初期データを準備中..." : loadingInfo.message} height="80vh" size={50} />;
    }

    if (error) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6">{error}</Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>
            戻る
          </Button>
        </Box>
      );
    }

    if (!response) {
      return (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          案件データが見つかりませんでした。
        </Typography>
      );
    }

    if (isEditing) {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        >
          <SalesProjectFormFields
            formData={editFormData}
            onChange={(field, value) => {
              setEditFormData((prev: any) => ({ ...prev, [field]: value }));
              // 入力時にそのフィールドのエラーを消す
              if (validationErrors[field]) {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[field];
                  return newErrors;
                });
              }
            }}
            currentTab={editTab}
            onTabChange={(tab) => setEditTab(tab)}
            errors={validationErrors}
            viewLevel={viewLevel}
          />
        </Box>
      );
    }

    // /////////////////////////////////////////////////////////////
    // ⬅️ メインリターン
    // /////////////////////////////////////////////////////////////
    return (
      <Box sx={{ p: 2 }}>
        {/* 上部: 案件名と担当者情報 (フル幅) */}
        <Box sx={{ mt: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2, bgcolor: 'white', p: 2, boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body1" color="text.secondary">案件名：</Typography>
              <Typography variant="h6" sx={{ color: 'grey.900' }}>{response.project_name}</Typography>
              {response.phase && (
                <Box sx={{ ml: 3 }}>
                  <SalesProjectPhaseChip
                    phase={response.phase}
                    sx={{ fontSize: '0.8125rem', height: 24 }}
                  />
                </Box>
              )}
              <SalesProjectTypeChip
                type={response.project_type}
                sx={{ fontSize: '0.8125rem', height: 24 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 0 }}>
              {response.created_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">作成日：</Typography>
                  <Typography variant="body2" color="text.secondary">{response.created_date}</Typography>
                </Box>
              )}
              {response.updated_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">最終更新日：</Typography>
                  <Typography variant="body2" color="text.secondary">{response.updated_date}</Typography>
                </Box>
              )}
              {response.updated_operator_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">最終更新者：</Typography>
                  <Typography variant="body2" color="text.secondary">{response.updated_operator_name}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              startIcon={<Icons.Edit />}
              onClick={() => {
                // 親子情報オブジェクトは更新payloadに不要なため除外し、
                // 親案件Autocompleteの表示用に親案件名だけを持ち込む
                const { parent_project, child_projects, ...rest } = response;
                setEditFormData({
                  ...rest,
                  parent_project_name: parent_project?.project_name || '',
                  // 分筆・合筆の未入力は既定の 1 筆 → 1 筆 として編集を開始する
                  parcel_count_before: rest.parcel_count_before ?? '1',
                  parcel_count_after: rest.parcel_count_after ?? '1',
                });
                setIsEditing(true);
              }}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              編集
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* 左側: 詳細情報 (8/12) */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ mt: 2, borderRadius: 2, bgcolor: 'white', p: 2, boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}>
              <Tabs
                value={inputGroup}
                onChange={(e, newValue) => setInputGroup(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab label="基本情報" value="basic" />
                <Tab label="土地" value="land" />
                <Tab label="建築" value="architecture" />
                <Tab label="フェーズ履歴" value="phasehistory" />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Icons.AttachFile sx={{ fontSize: '1.2rem' }} />
                      添付ファイル
                      {filesLoading ? (
                        <CircularProgress
                          size={14}
                          thickness={5}
                          sx={{
                            ml: 0.5,
                            color: inputGroup === 'attachments' ? 'primary.main' : 'grey.400',
                          }}
                        />
                      ) : (
                        uploadedFiles.length > 0 && (
                          <Chip
                            label={uploadedFiles.length}
                            size="small"
                            sx={{
                              height: 18,
                              minWidth: 18,
                              ml: 0.5,
                              bgcolor: inputGroup === 'attachments' ? 'white' : 'primary.main',
                              color: inputGroup === 'attachments' ? 'primary.main' : 'white',
                              '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }
                            }}
                          />
                        )
                      )}
                    </Box>
                  }
                  value="attachments"
                />
              </Tabs>

              {inputGroup === 'basic' && (
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="レコードタイプ" icon={<Icons.Description />}>{response.record_type}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="案件種別" icon={<Icons.CallSplit />}>
                      {response.project_type ? (PROJECT_TYPE_LABELS[response.project_type] ?? response.project_type) : '未分類'}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DetailField label="取引先" icon={<Icons.Person />}>
                      {response.client_id ? (
                        <Link
                          component="button"
                          type="button"
                          onClick={() => setCustomerDialogOpen(true)}
                          sx={{
                            textDecoration: 'none',
                            fontSize: 'inherit',
                            verticalAlign: 'baseline',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {response.client_name}
                        </Link>
                      ) : (
                        response.client_name
                      )}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="大項目" icon={<Icons.Bookmark />}>{response.category_large}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="大項目 予備" icon={<Icons.Bookmark />}>{response.category_large_extra}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="中項目" icon={<Icons.ListAlt />}>{response.category_medium}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="中項目 予備" icon={<Icons.ListAlt />}>{response.category_medium_extra}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="アポイント取得方法" icon={<Icons.Handshake />}>{response.appointment_method}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="案件化手法" icon={<Icons.Search />}>{response.lead_generation_method}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField
                      label={response.project_type === 'purchase' ? '決済日' : '受注確定日'}
                      icon={<Icons.EventAvailable />}
                    >
                      {response.order_confirmed_date}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="工事案件番号" icon={<Icons.Code />}>
                      {Array.isArray(response.new_system_number)
                        ? response.new_system_number.map((num: string, idx: number) => (
                            <Chip key={idx} label={num} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))
                        : response.new_system_number}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DetailField label="不動産業者(管理・仲介)" icon={<Icons.Business />}>{response.real_estate_agency_mgmt}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DetailField label="不動産業者(売却仲介)" icon={<Icons.Business />}>{response.real_estate_agency_brokerage}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="不動産取得税" icon={<Icons.Payments />}>
                      {response.real_estate_acquisition_tax != null && response.real_estate_acquisition_tax !== ''
                        ? `${Number(response.real_estate_acquisition_tax).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="固都税" icon={<Icons.Payments />}>
                      {response.fixed_asset_city_tax != null && response.fixed_asset_city_tax !== ''
                        ? `${Number(response.fixed_asset_city_tax).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    {/* 火災保険は支払先・金額とも全社一律の固定契約 */}
                    <DetailField label="火災保険 (固定)" icon={<Icons.Payments />}>
                      {`${FIRE_INSURANCE_PAYEE} ${FIRE_INSURANCE_AMOUNT.toLocaleString()} 円`}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="所有権移転費用" icon={<Icons.Payments />}>
                      {response.ownership_transfer_payee || (response.ownership_transfer_cost != null && response.ownership_transfer_cost !== '')
                        ? `${response.ownership_transfer_payee ? `${response.ownership_transfer_payee} ` : ''}${response.ownership_transfer_cost != null && response.ownership_transfer_cost !== '' ? `${Number(response.ownership_transfer_cost).toLocaleString()} 円` : ''}`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField
                      label={
                        response.project_type === 'purchase'
                          ? '仕入時仲介手数料'
                          : response.project_type === 'sales'
                            ? '売却時仲介手数料'
                            : '仲介手数料'
                      }
                      icon={<Icons.Payments />}
                    >
                      {response.brokerage_fee != null && response.brokerage_fee !== ''
                        ? `${Number(response.brokerage_fee).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DetailField label="金融機関" icon={<Icons.AccountBalance />}>{response.financial_institution}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <DetailField label="中長期追客予定" icon={<Icons.AccessTime />}>{response.long_term_follow_up === 1 ? '中長期' : '通常'}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <DetailField label={response.project_type === 'purchase' ? '契約予定年月' : '受注予定年月'} icon={<Icons.CalendarToday />}>
                      {response.expected_order_year || response.expected_order_month
                        ? `${response.expected_order_year ? `${response.expected_order_year}年` : ''}${response.expected_order_month ? `${response.expected_order_month}月` : ''}`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <DetailField label={response.project_type === 'purchase' ? '契約金額' : '売上'} icon={<Icons.TrendingUp />}>
                      {response.order_sales_amount != null && response.order_sales_amount !== ''
                        ? `${Number(response.order_sales_amount).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DetailField label="問題点" icon={<Icons.InfoOutlined />} multiline>{response.issues}</DetailField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DetailField label="備考" icon={<Icons.ChatBubbleOutline />} multiline>{response.remarks}</DetailField>
                  </Grid>

                  {response.phase === '7.失注' && (
                    <Grid size={{ xs: 12 }}>
                      <DetailField label="失注理由" icon={<Icons.InfoOutlined />} multiline>
                        {response.lost_reason_name || response.lost_reason_detail
                          ? `${response.lost_reason_name ? `【${response.lost_reason_name}】` : ''}${response.lost_reason_detail ? `\n${response.lost_reason_detail}` : ''}`
                          : null}
                      </DetailField>
                    </Grid>
                  )}

                </Grid>
              )}

              {inputGroup === 'phasehistory' && response?.project_id && (
                <SalesProjectPhaseHistory projectId={response.project_id} />
              )}

              {inputGroup === 'land' && (
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12 }}>
                    <DetailField label="土地名称" icon={<Icons.Landscape />}>{response.land_name}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="敷地面積 (㎡)" icon={<Icons.SquareFoot />}>
                      {response.land_area != null && response.land_area !== ''
                        ? `${Number(response.land_area).toLocaleString(undefined, { maximumFractionDigits: 2 })} ㎡ (約 ${sqmToTsubo(response.land_area)!.toFixed(2)} 坪)`
                        : null}
                    </DetailField>
                  </Grid>
                  {response.project_type === 'purchase' ? (
                    <Grid size={{ xs: 12, md: 6 }}>
                      {/* 元の登記筆数 → 移転登記後の筆数 (仕入案件のみ表示)。未入力は既定の 1 とみなし、増減がある場合のみ合筆/分筆を添える */}
                      <DetailField label="分筆・合筆" icon={<Icons.CallSplit />}>
                        {(() => {
                          const before = Number(response.parcel_count_before ?? 1) || 1;
                          const after = Number(response.parcel_count_after ?? 1) || 1;
                          let suffix = '';
                          if (before > after) suffix = ' (合筆)';
                          else if (before < after) suffix = ' (分筆)';
                          return `${before.toLocaleString()} 筆 → ${after.toLocaleString()} 筆${suffix}`;
                        })()}
                      </DetailField>
                    </Grid>
                  ) : (
                    // 売上案件では分筆・合筆を出さず、費用4項目を次行へ送る行埋めスペーサーを置く
                    <Grid size={{ md: 6 }} sx={{ display: { xs: 'none', md: 'block' } }} />
                  )}
                  {/* 費用項目 (1行並び)。金額入力ありで「あり」扱い、金額未入力の既存案件は旧区分 (あり/なし) を表示 */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <DetailField label="解体" icon={<Icons.Engineering />}>
                      {response.demolition_cost != null && response.demolition_cost !== ''
                        ? `あり (${Number(response.demolition_cost).toLocaleString()} 円)`
                        : response.has_demolition === true ? 'あり' : response.has_demolition === false ? 'なし' : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <DetailField label="造成" icon={<Icons.Engineering />}>
                      {response.land_prep_cost != null && response.land_prep_cost !== ''
                        ? `あり (${Number(response.land_prep_cost).toLocaleString()} 円)`
                        : response.has_land_prep === true ? 'あり' : response.has_land_prep === false ? 'なし' : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <DetailField label="給排水" icon={<Icons.Payments />}>
                      {response.water_supply_cost != null && response.water_supply_cost !== ''
                        ? `あり (${Number(response.water_supply_cost).toLocaleString()} 円)`
                        : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    {/* 金額入力ありで「あり」扱い。金額未入力の既存案件は旧区分 (未/済) を表示 */}
                    <DetailField label="確定測量" icon={<Icons.FactCheck />}>
                      {response.land_survey_cost != null && response.land_survey_cost !== ''
                        ? `あり (${Number(response.land_survey_cost).toLocaleString()} 円)`
                        : response.land_survey_confirmed}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="坪単価(想定)" icon={<Icons.TrendingUp />}>
                      {response.estimated_price_per_tsubo != null && response.estimated_price_per_tsubo !== ''
                        ? `${Number(response.estimated_price_per_tsubo).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="坪単価(希望)" icon={<Icons.TrendingUp />}>
                      {response.desired_price_per_tsubo != null && response.desired_price_per_tsubo !== ''
                        ? `${Number(response.desired_price_per_tsubo).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="想定買取可能額" icon={<Icons.TrendingUp />}>
                      {response.estimated_purchase_price != null && response.estimated_purchase_price !== ''
                        ? `${Number(response.estimated_purchase_price).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="売却希望額" icon={<Icons.TrendingUp />}>
                      {response.desired_selling_price != null && response.desired_selling_price !== ''
                        ? `${Number(response.desired_selling_price).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>
                </Grid>
              )}

              {inputGroup === 'architecture' && (
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12 }}>
                    <ProjectBuildingLinks
                      projectId={response.project_id}
                      links={response.building_links || []}
                      onChanged={fetchDetail}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailField label="建物１" icon={<Icons.HomeWork />}>{response.building_1}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailField label="建物２" icon={<Icons.HomeWork />}>{response.building_2}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailField label="建物３" icon={<Icons.HomeWork />}>{response.building_3}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailField label="建物４" icon={<Icons.HomeWork />}>{response.building_4}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="建築費種類" icon={<Icons.Engineering />}>{response.building_cost_type}</DetailField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DetailField label="想定 工事費合計" icon={<Icons.TrendingUp />}>
                      {response.estimated_total_construction_cost != null && response.estimated_total_construction_cost !== ''
                        ? `${Number(response.estimated_total_construction_cost).toLocaleString()} 円`
                        : null}
                    </DetailField>
                  </Grid>
                </Grid>
              )}

              {inputGroup === 'attachments' && response?.project_id && (
                <Box sx={{ p: 2 }}>
                  <Box sx={{ p: 1 }}>
                    {/* アップロードエリア */}
                    <Box sx={{ mb: 2, py: 1.5, px: 2, border: '2px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Icons.CloudUpload sx={{ fontSize: 24, color: 'text.secondary', mb: 0.5, opacity: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        ファイルをドラッグ＆ドロップ、または選択してください
                      </Typography>
                      <input
                        accept="image/jpeg,image/png,application/pdf"
                        style={{ display: 'none' }}
                        id="detail-raised-button-file"
                        multiple
                        type="file"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="detail-raised-button-file">
                        <Button variant="contained" size="small" component="span" startIcon={<Icons.Add />}>
                          ファイルを選択
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
                        対応形式: PDF, JPEG, PNG
                      </Typography>
                    </Box>

                    {/* アップロード進捗 */}
                    {uploading && (
                      <Box sx={{ width: '100%', mb: 2 }}>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                          アップロード中...
                        </Typography>
                      </Box>
                    )}

                    {/* ファイル一覧 */}
                    {filesLoading ? (
                      <LoadingView message="読み込み中..." height="100px" size={30} />
                    ) : uploadedFiles.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        添付されているファイルはありません。
                      </Typography>
                    ) : (
                      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <List disablePadding>
                          {uploadedFiles.map((file, idx) => {
                            const isImage = file.contentType?.startsWith('image/');
                            const isPdf = file.contentType === 'application/pdf';

                            return (
                              <React.Fragment key={idx}>
                                {idx > 0 && <Divider />}
                                <ListItem
                                  onClick={() => {
                                    if (isImage || isPdf) {
                                      setPreviewFile({ url: file.url, name: file.name, contentType: file.contentType });
                                    } else {
                                      window.open(file.url, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                  secondaryAction={
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFileToDelete({ name: file.name });
                                        setDeleteFileDialogOpen(true);
                                      }}
                                      title="削除"
                                    >
                                      <Icons.Delete sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  }
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: 'grey.50',
                                      '& .file-name': {
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                      },
                                    },
                                    py: 1
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 40 }}>
                                    {isImage ? (
                                      <Icons.Image color="primary" sx={{ fontSize: 24 }} />
                                    ) : isPdf ? (
                                      <Icons.PictureAsPdf color="error" sx={{ fontSize: 24 }} />
                                    ) : (
                                      <Icons.InsertDriveFile color="action" sx={{ fontSize: 24 }} />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={file.name}
                                    secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB • ${format(new Date(file.updated), 'yyyy/MM/dd HH:mm')}`}
                                    primaryTypographyProps={{
                                      className: 'file-name',
                                      variant: 'body2',
                                      fontWeight: 'medium',
                                      sx: {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 'calc(100% - 100px)'
                                      }
                                    }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItem>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Paper>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

          </Grid>

          {/* 右側: 担当チーム & 活動履歴 (4/12) */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* 関連案件カード（親の仕入案件 / 派生した売上案件） */}
            {(response.parent_project || (response.child_projects && response.child_projects.length > 0) || response.project_type === 'purchase') && (
              <Box sx={{ mt: 2, bgcolor: 'white', borderRadius: 2, p: 2, boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'white',
                      width: 25,
                      height: 25,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icons.AccountTree fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" color="text.secondary">関連案件</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {response.parent_project && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        親案件（仕入）
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SalesProjectTypeChip type={response.parent_project.project_type} />
                        <Link
                          component="button"
                          type="button"
                          onClick={() => navigate(`/salesprojectsmanagement/${response.parent_project.project_id}`)}
                          sx={{
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            textAlign: 'left',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {response.parent_project.project_name}
                        </Link>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, ml: 0.25 }}>
                        <SalesProjectPhaseChip phase={response.parent_project.phase} />
                        {response.parent_project.client_name && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {response.parent_project.client_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {response.child_projects && response.child_projects.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        売上案件（{response.child_projects.length}件）
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {response.child_projects.map((child: any) => (
                          <Box key={child.project_id}>
                            <Link
                              component="button"
                              type="button"
                              onClick={() => navigate(`/salesprojectsmanagement/${child.project_id}`)}
                              sx={{
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textAlign: 'left',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {child.project_name}
                            </Link>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                              <SalesProjectPhaseChip phase={child.phase} />
                              {child.order_sales_amount != null && (
                                <Typography variant="caption" color="text.secondary">
                                  ¥{Number(child.order_sales_amount).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {response.project_type === 'purchase' && (
                    <Button
                      startIcon={<Icons.Add />}
                      onClick={() => navigate('/salesprojectscreate', {
                        state: {
                          presetParentId: response.project_id,
                          presetParentName: response.project_name,
                          presetClientId: response.client_id,
                          presetClientName: response.client_name,
                        },
                      })}
                      sx={[STANDARD_CTA_BUTTON_SX, { alignSelf: 'flex-start', mt: 0.5 }]}
                    >
                      売上案件を作成
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* 担当チームカード */}
            {(() => {
              const currentUserStaffName = user?.email
                ? (staffList.find(s => s.staff_id === user.email)?.staff_name || '')
                : '';
              const members: { name: string; role: string; department?: string }[] = [];
              if (response.main_staff_name) {
                members.push({
                  name: response.main_staff_name,
                  role: 'メイン担当',
                  department: response.main_staff_department_name || response.main_staff_department,
                });
              }
              if (response.owner_name) {
                members.push({
                  name: response.owner_name,
                  role: '案件所有者',
                  department: response.owner_department_name || response.owner_department,
                });
              }
              if (response.support_staff_name) {
                members.push({
                  name: response.support_staff_name,
                  role: 'サポート',
                  department: response.support_staff_department_name || response.support_staff_department,
                });
              }
              if (members.length === 0) return null;

              return (
                <Box sx={{ mt: 2, bgcolor: 'white', borderRadius: 2, p: 2, boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        bgcolor: 'primary.light',
                        color: 'white',
                        width: 25,
                        height: 25,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icons.People fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" color="text.secondary">担当チーム</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {members.map((m, idx) => {
                      const isSelf = !!currentUserStaffName && m.name === currentUserStaffName;
                      const initial = (m.name || '').trim().charAt(0);
                      return (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: blueGrey[50],
                              color: 'primary.main',
                              fontSize: '0.95rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {initial}
                          </Avatar>
                          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                              {m.name}{isSelf ? '（自分）' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                              {m.department ? `${m.role} / ${m.department}` : m.role}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}

            <Box sx={{ mt: 2, bgcolor: 'white', borderRadius: 2, p: 2, boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.light',
                    color: 'white',
                    width: 25,
                    height: 25,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icons.Task />
                </Box>
                <Typography variant="subtitle1" color="text.secondary">活動履歴</Typography>
                <IconButton
                  size="small"
                  color="primary"
                  sx={{
                    ml: 'auto',
                    width: 23,
                    height: 23,
                    bgcolor: 'primary.light',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.main' }
                  }}
                  onClick={() => handleOpenActivityDialog()}
                >
                  <Icons.Add fontSize="small" />
                </IconButton>
              </Box>

              {activitiesLoading ? (
                <LoadingView message="読み込み中..." height="120px" size={30} />
              ) : activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  活動履歴がありません。
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {activities.map((activity, idx) => (
                    <Card key={activity.auto_number || idx} variant="outlined" sx={{ borderRadius: 1, minWidth: 0, overflow: 'visible', height: 'auto' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 2 } }}>
                        {/* 上部: タイトルと日付 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            sx={{
                              fontWeight: 'bold',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              flex: 1,
                              display: 'block'
                            }}
                          >
                            {activity.activity_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              mt: 0.3,
                              display: 'block'
                            }}
                          >
                            期日 : {activity.due_date}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setMenuActivity(activity);
                            }}
                            sx={{ mt: -0.5, mr: -1 }}
                          >
                            <Icons.MoreVert fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* 下部: 説明文と担当者 */}
                        <Box sx={{ display: 'block' }}>
                          {activity.activity_content && (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.5,
                                mb: 1,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                display: 'block'
                              }}
                            >
                              {activity.activity_content}
                            </Typography>
                          )}
                          {activity.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                mb: 1,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                display: 'block'
                              }}
                            >
                              <Chip
                                label='備 考'
                                sx={{
                                  mr: 1,
                                  px: 0,
                                  bgcolor: blueGrey[200],
                                  color: 'white',
                                  fontSize: '10px',
                                  borderRadius: 1,
                                  height: '18px'
                                }}
                              />
                              {activity.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ overflowWrap: 'anywhere', display: 'block' }}
                            >
                              担当: {activity.assign_to}
                            </Typography>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* 活動履歴操作メニュー */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleOpenActivityDialog(menuActivity)}>
            <Icons.Edit sx={{ fontSize: 18, mr: 1 }} /> 編集
          </MenuItem>
          <MenuItem onClick={() => setDeleteDialogOpen(true)} sx={{ color: 'error.main' }}>
            <Icons.Delete sx={{ fontSize: 18, mr: 1 }} /> 削除
          </MenuItem>
        </Menu>

        {/* 活動履歴削除確認ダイアログ */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>活動履歴の削除</DialogTitle>
          <DialogContent>
            <Typography>この活動履歴を削除してもよろしいですか？</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
              キャンセル
            </Button>
            <Button onClick={handleDeleteActivity} color="error" variant="contained">
              削除する
            </Button>
          </DialogActions>
        </Dialog>


        {/* 活動履歴登録・編集ダイアログ */}
        <Dialog open={activityDialogOpen} onClose={() => setActivityDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingActivity ? '活動履歴の編集' : '活動履歴の追加'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="件名"
                fullWidth
                size="small"
                value={activityForm.activity_name}
                onChange={(e) => setActivityForm({ ...activityForm, activity_name: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { fontSize: '13px' },
                  '& .MuiOutlinedInput-input': { fontSize: '13px' },
                }}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                <DatePicker
                  label="期日"
                  value={new Date(activityForm.due_date)}
                  onChange={(date: Date | null) => date && setActivityForm({ ...activityForm, due_date: format(date, 'yyyy-MM-dd') })}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                />
              </LocalizationProvider>
              <Autocomplete
                options={staffList}
                getOptionLabel={(option) => option.staff_name || ''}
                value={staffList.find(s => s.staff_id === activityForm.assign_to_id) || staffList.find(s => s.staff_name === activityForm.assign_to) || null}
                inputValue={assignToInput}
                onInputChange={(event, newInputValue) => {
                  setAssignToInput(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setActivityForm({
                    ...activityForm,
                    assign_to: newValue ? newValue.staff_name : '',
                    assign_to_id: newValue ? newValue.staff_id : ''
                  });
                }}
                isOptionEqualToValue={(option, value) => option.staff_id === value.staff_id}
                sx={{
                  width: '50%',
                  backgroundColor: 'background.paper',
                  '& .MuiInputLabel-root': { fontSize: '13px' },
                  '& .MuiOutlinedInput-input': { fontSize: '13px' },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="担当者" size="small" fullWidth />
                )}
              />
              <TextField
                label="活動内容"
                fullWidth
                multiline
                rows={4}
                size="small"
                value={activityForm.activity_content}
                onChange={(e) => setActivityForm({ ...activityForm, activity_content: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { fontSize: '13px' },
                  '& .MuiOutlinedInput-input': { fontSize: '13px' },
                }}
              />
              <TextField
                label="備考"
                fullWidth
                multiline
                rows={4}
                size="small"
                value={activityForm.description}
                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { fontSize: '13px' },
                  '& .MuiOutlinedInput-input': { fontSize: '13px' },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setActivityDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
              キャンセル
            </Button>
            <Button
              onClick={handleActivitySave}
              disabled={!activityForm.activity_name}
              startIcon={<Icons.Save />}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // --------------------------------------------------
  // 🏗️ レイアウト構築
  // --------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* 一覧に戻る (詳細表示時のみ。編集モード中はキャンセル/保存が代替するため非表示) */}
        {!isEditing && (
          <Box sx={{ ml: 3, mb: 1 }}>
            <Button
              startIcon={<Icons.ArrowBack />}
              onClick={() => {
                if (onClose) onClose();
                else navigate('/salesprojectsmanagement');
              }}
              sx={STANDARD_TEXT_BUTTON_SX}
            >
              一覧に戻る
            </Button>
          </Box>
        )}
        <Box sx={{ mt: 3, mb: 0, ml: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: 'primary.light',
                color: 'white',
                width: 28,
                height: 28,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icons.Domain sx={{ fontSize: '1.25rem' }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              <Typography variant="h6" color="text.secondary">
                {isEditing ? '案件編集' : '案件詳細'}
              </Typography>
              <Typography variant="subtitle1" color="text.primary">
                ID : {projectId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {isEditing && (
              <>
                <Button onClick={() => setIsEditing(false)} sx={STANDARD_TEXT_BUTTON_SX}>
                  キャンセル
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <Icons.ArrowDropDown /> : <Icons.Save />}
                  sx={STANDARD_CTA_BUTTON_SX}
                >
                  {saving ? '保存中...' : '保存する'}
                </Button>
              </>
            )}
          </Box>
        </Box>
        {renderContent()}
      </Container>

      {/* 失注理由入力ダイアログ（フェーズ→7.失注 の保存時に必須） */}
      <SalesProjectLostReasonDialog
        open={lostReasonDialogOpen}
        initialValue={{
          lost_reason_category: editFormData?.lost_reason_category || '',
          lost_reason_detail: editFormData?.lost_reason_detail || '',
        }}
        onCancel={() => setLostReasonDialogOpen(false)}
        onConfirm={handleLostReasonConfirm}
      />

      {/* 保存確認ダイアログ */}
      <Dialog open={confirmSaveDialogOpen} onClose={() => setConfirmSaveDialogOpen(false)}>
        <DialogTitle>保存の確認</DialogTitle>
        <DialogContent>
          <Typography>案件情報を更新してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmSaveDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleConfirmSave} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} autoFocus>
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知用スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 画像プレビューダイアログ */}
      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        maxWidth="xl"
        fullWidth
        // ダイアログ展開時に body へ padding-right が付与され、背後のメイン画面右側に
        // 余白が生じる（スクロールバー分のレイアウトシフト）のを防ぐ
        disableScrollLock
      >
        <DialogTitle sx={{ m: 0, py: 0.25, px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.8125rem' }}>{previewFile?.name}</Typography>
          <IconButton size="small" onClick={() => setPreviewFile(null)} sx={{ p: 0.5 }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', height: previewFile?.contentType === 'application/pdf' ? '80vh' : 'auto' }}>
          {previewFile?.contentType === 'application/pdf' ? (
            <Box
              component="iframe"
              src={`${previewFile?.url}#toolbar=0`}
              sx={{ width: '100%', height: '100%', border: 'none' }}
              title="PDF Preview"
            />
          ) : (
            <Box
              component="img"
              src={previewFile?.url}
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ py: 0.25, px: 1 }}>
          <Button size="small" component="a" href={previewFile?.url} download={previewFile?.name} startIcon={<Icons.Save />} sx={{ minHeight: 0, py: 0.25 }}>
            ダウンロード
          </Button>
          <Button size="small" onClick={() => setPreviewFile(null)} sx={{ minHeight: 0, py: 0.25 }}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* ファイル削除確認ダイアログ */}
      <Dialog open={deleteFileDialogOpen} onClose={() => setDeleteFileDialogOpen(false)}>
        <DialogTitle>ファイルの削除</DialogTitle>
        <DialogContent>
          <Typography>「{fileToDelete?.name}」を削除してもよろしいですか？</Typography>
          <Typography variant="caption" color="error">※この操作は取り消せません</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteFileDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleDeleteFile} color="error" variant="contained">
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 取引先（顧客）詳細モーダル */}
      {customerDialogOpen && response?.client_id && (
        <CrmCustomerForm
          customerId={response.client_id}
          open={customerDialogOpen}
          onClose={() => setCustomerDialogOpen(false)}
          onSuccess={() => {
            setCustomerDialogOpen(false);
            fetchDetail();
          }}
        />
      )}
    </>
  );
}
